import React, { useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Box,
  Chip,
  useTheme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  CustomMenu,
  CustomMenuItem,
  StyledTableRow,
} from "../../../../../styles/Shipmentstyles";
import { Employee } from "../../../../../models/Employees/Employee";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import { useStores } from "../../../use-stores";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";

interface EmployeeTableProp {
  employees: Employee[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onEditClick: (employeeId: number) => void;
  onViewClick: (employeeId: number) => void;
  onDeleteClick: (employeeId: number) => void;
}

const EmployeeTable = ({
  employees,
  page_no,
  limit,
  total_pages,
  total_count,
  onPageChange,
  onRowsPerPageChange,
  onEditClick,
  onViewClick,
  onDeleteClick,
}: EmployeeTableProp) => {
  const theme = useTheme();
  const { userStore } = useStores();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElRestore, setAnchorElRestore] = useState<null | HTMLElement>(
    null,
  );
  const [selectedEmpId, setSelectedEmpId] = useState<number>();

  const user_managerteam_map = new Map<number, number>();
  userStore.user?.teams?.forEach((team) => {
    if (team.is_manager) user_managerteam_map.set(team.id, team.id);
  });

  const isOpen = Boolean(anchorEl);
  const isOpenRestore = Boolean(anchorElRestore);
  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    employeeId: number,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmpId(employeeId);
  };

  const handleMenuClickRestore = (
    event: React.MouseEvent<HTMLButtonElement>,
    employeeId: number,
  ) => {
    setAnchorElRestore(event.currentTarget);
    setSelectedEmpId(employeeId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setAnchorElRestore(null);
    setSelectedEmpId(undefined);
  };

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const canEditEmployeeDetails = (employee: Employee) => {
    if (userStore.isSuperAdmin()) return true;

    const filtered_teams =
      employee.teams?.filter(
        (team) => user_managerteam_map.get(team.id) !== undefined,
      ) || [];
    return filtered_teams?.length !== 0;
  };

  return (
    <Paper
      sx={{
        borderRadius: 2,
        boxShadow:
          "rgba(0, 0, 0, 0.08) 0px 2px 16px, rgba(0, 0, 0, 0.04) 0px 4px 24px",
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <TableContainer sx={{ maxHeight: window.innerHeight - 340 }}>
        <Table stickyHeader size="medium">
          <TableHead>
            <TableRow>
              <AppTableHeaderCell
                title="ID"
                sx={{ textAlign: "center", minWidth: "60px" }}
              />
              <AppTableHeaderCell
                title="Name"
                sx={{ textAlign: "center", minWidth: "120px" }}
              />
              <AppTableHeaderCell
                title="Email"
                sx={{ textAlign: "center", minWidth: "160px" }}
              />
              <AppTableHeaderCell
                title="Phone Number"
                sx={{ textAlign: "center", minWidth: "120px" }}
              />
              <AppTableHeaderCell
                title="Discount %"
                sx={{ textAlign: "center", minWidth: "100px" }}
              />
              <AppTableHeaderCell
                title="Status"
                sx={{ textAlign: "center", minWidth: "100px" }}
              />
              <AppTableHeaderCell
                title="Updated By"
                sx={{ textAlign: "center", minWidth: "100px" }}
              />
              <AppTableHeaderCell
                title="Updated At"
                sx={{ textAlign: "center", minWidth: "110px" }}
              />
              <AppTableHeaderCell
                title="Created By"
                sx={{ textAlign: "center", minWidth: "100px" }}
              />
              <AppTableHeaderCell
                title="Created At"
                sx={{ textAlign: "center", minWidth: "110px" }}
              />
              <AppTableHeaderCell
                title="Action"
                sx={{ textAlign: "center", width: "10%", minWidth: "80px" }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((employee: Employee) => {
              const canViewRow =
                !Boolean(employee.is_deleted) &&
                (canEditEmployeeDetails(employee) ||
                  employee.id === userStore.user?.id);

              return (
                <StyledTableRow
                  key={employee.id}
                  onClick={
                    canViewRow
                      ? () => onViewClick(Number(employee.id))
                      : undefined
                  }
                  sx={{
                    cursor: canViewRow ? "pointer" : "default",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <AppTableRowCell>{employee.id}</AppTableRowCell>
                  <AppTableRowCell>{employee.name || "NA"}</AppTableRowCell>
                  <AppTableRowCell>{employee.email}</AppTableRowCell>
                  <AppTableRowCell>{employee.phone_number}</AppTableRowCell>
                  <AppTableRowCell>
                    {employee.discount_percentage || "-"}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    <Chip
                      label={
                        Boolean(employee.is_deleted) ? "Inactive" : "Active"
                      }
                      size="small"
                      color={Boolean(employee.is_deleted) ? "error" : "success"}
                      variant="outlined"
                      sx={{
                        fontWeight: 500,
                        borderRadius: "6px",
                      }}
                    />
                  </AppTableRowCell>
                  <AppTableRowCell>
                    {employee.updated_by_name || "-"}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    {formatDate(employee.modified_date)}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    {employee.created_by_name || "-"}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    {formatDate(employee.created_date)}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ textAlign: "center", width: "10%" }}>
                    {canEditEmployeeDetails(employee) ||
                    employee.id === userStore.user?.id ? (
                      Boolean(employee.is_deleted) ? (
                        <Box>
                          {/* <IconButton
                              onClick={(event) => {
                                handleMenuClickRestore(
                                  event,
                                  Number(employee.id)
                                );
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                            <CustomMenu
                              anchorEl={anchorElRestore}
                              open={isOpenRestore}
                              onClose={handleMenuClose}
                              anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                            >
                              <CustomMenuItem
                                onClick={() => {
                                  handleMenuClose();
                                  onEditClick(Number(selectedEmpId));
                                }}
                              >
                                Restore
                              </CustomMenuItem>
                            </CustomMenu> */}
                        </Box>
                      ) : (
                        <Box>
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleMenuClick(event, Number(employee.id));
                            }}
                            sx={{
                              "&:hover": {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                          <CustomMenu
                            anchorEl={anchorEl}
                            open={isOpen}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                          >
                            <CustomMenuItem
                              onClick={() => {
                                handleMenuClose();
                                onEditClick(Number(selectedEmpId));
                              }}
                            >
                              View
                            </CustomMenuItem>
                            {userStore.isSuperAdminManager() && (
                              <CustomMenuItem
                                onClick={() => {
                                  handleMenuClose();
                                  onDeleteClick(Number(selectedEmpId));
                                }}
                              >
                                Delete
                              </CustomMenuItem>
                            )}
                          </CustomMenu>
                        </Box>
                      )
                    ) : (
                      ""
                    )}
                  </AppTableRowCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[30, 50, 100, 150, 200]}
        component="div"
        count={total_count}
        rowsPerPage={limit}
        page={page_no - 1}
        onPageChange={(event, page_no) => onPageChange(event, page_no + 1)}
        onRowsPerPageChange={(event) =>
          handleRowsChange(parseInt(event.target.value, 10))
        }
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
            {
              fontSize: "0.875rem",
            },
        }}
      />
    </Paper>
  );
};

export default EmployeeTable;
